import { useCallback, useEffect, useRef, useState } from "react";
import {
  FieldValues,
  UseFormProps,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Tooltip } from "../components/tooltip";
import { Button } from "../controls/buttons/button";
import { IconButton } from "../controls/buttons/icon-button";
import { CircleExclamationRegular, CircleXmarkRegular } from "../icons";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "./draft-storage";

const DEBOUNCE_MS = 500;

interface UseFormDraftOptions<T extends FieldValues> extends UseFormProps<T> {
  draftKey: string;
  entityCode?: string;
  orgCode: string;
  excludeFields?: string[];
  onError?: (error: unknown, context: string) => void;
}

interface UseFormDraftReturn<T extends FieldValues> {
  formMethods: UseFormReturn<T>;
  hasDraft: boolean;
  restoreDraft: () => void;
  clearDraft: () => void;
  DraftBanner: React.FC;
  ClearDraftButton: React.FC;
}

function stripFields(
  data: Record<string, unknown>,
  excludeFields: string[],
): Record<string, unknown> {
  const result = { ...data };
  for (const field of excludeFields) {
    delete result[field];
  }
  return result;
}

export function useFormDraft<T extends FieldValues>(
  options: UseFormDraftOptions<T>,
): UseFormDraftReturn<T> {
  const { t } = useTranslation();
  const {
    draftKey,
    entityCode,
    orgCode,
    excludeFields = [],
    onError,
    ...useFormProps
  } = options;

  const formMethods = useForm<T>(useFormProps);
  const [hasDraft, setHasDraft] = useState(false);
  const hasDraftRef = useRef(false);
  const draftDataRef = useRef<Record<string, unknown> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringRef = useRef(false);

  const storageKey = buildDraftKey(orgCode, draftKey, entityCode);
  const storageKeyRef = useRef(storageKey);

  const handleClearDraft = useCallback(() => {
    clearDraft(storageKey);
    setHasDraft(false);
    hasDraftRef.current = false;
    draftDataRef.current = null;
  }, [storageKey]);

  const restoreDraft = useCallback(() => {
    if (!draftDataRef.current) return;
    isRestoringRef.current = true;
    formMethods.reset(draftDataRef.current as T, { keepDefaultValues: true });
    setHasDraft(false);
    hasDraftRef.current = false;
    requestAnimationFrame(() => {
      isRestoringRef.current = false;
    });
  }, [formMethods]);

  const resetForm = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  // Load draft on mount or when storageKey changes
  useEffect(() => {
    if (storageKeyRef.current !== storageKey) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      storageKeyRef.current = storageKey;
    }

    const draft = loadDraft(storageKey, undefined, onError);
    if (!draft) {
      setHasDraft(false);
      hasDraftRef.current = false;
      draftDataRef.current = null;
      return;
    }

    const isEditForm = entityCode !== undefined;

    if (isEditForm) {
      const defaultValues = useFormProps.defaultValues;
      if (
        defaultValues &&
        typeof defaultValues === "object" &&
        !(defaultValues instanceof Promise)
      ) {
        const serverJson = JSON.stringify(defaultValues);
        const draftJson = JSON.stringify(draft.data);
        if (serverJson === draftJson) {
          clearDraft(storageKey);
          setHasDraft(false);
          hasDraftRef.current = false;
          draftDataRef.current = null;
          return;
        }
      }
      draftDataRef.current = draft.data;
      setHasDraft(true);
      hasDraftRef.current = true;
    } else {
      formMethods.reset(draft.data as T);
      draftDataRef.current = draft.data;
      setHasDraft(true);
      hasDraftRef.current = true;
    }
  }, [storageKey, entityCode]);

  // Auto-save with debounce
  useEffect(() => {
    const subscription = formMethods.watch((values, { name }) => {
      // Auto-dismiss banner when user changes a specific field (name is set).
      // Programmatic reset() fires with name=undefined, so it won't dismiss.
      if (hasDraftRef.current && name && !isRestoringRef.current) {
        setHasDraft(false);
        hasDraftRef.current = false;
        draftDataRef.current = null;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const data =
          excludeFields.length > 0
            ? stripFields(values as Record<string, unknown>, excludeFields)
            : (values as Record<string, unknown>);
        saveDraft(storageKeyRef.current, data, onError);
      }, DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formMethods, excludeFields, onError]);

  const DraftBanner = useCallback(
    () =>
      hasDraft && entityCode ? (
        <div className="flex items-center gap-3 rounded-lg bg-yellow-50 border border-yellow-300 px-4 py-3 mb-4 text-yellow-900">
          <CircleExclamationRegular width={20} />
          <span className="flex-1 text-sm font-medium">
            {t("draftBannerMessage")}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="small"
              variant="solid"
              contained
              onClick={restoreDraft}
            >
              {t("draftBannerRestore")}
            </Button>
            <Button
              type="button"
              size="small"
              variant="outline"
              contained
              onClick={handleClearDraft}
            >
              {t("draftBannerDiscard")}
            </Button>
          </div>
        </div>
      ) : null,
    [hasDraft, entityCode, restoreDraft, handleClearDraft, t],
  );

  const ClearDraftButton = useCallback(
    () => (
      <Tooltip label={t("clearDraftTooltip")}>
        <IconButton
          customIcon={<CircleXmarkRegular />}
          onClick={resetForm}
          size="small"
          color="text-gray-400 border-gray-300"
        />
      </Tooltip>
    ),
    [resetForm, t],
  );

  return {
    formMethods,
    hasDraft,
    restoreDraft,
    clearDraft: handleClearDraft,
    DraftBanner,
    ClearDraftButton,
  };
}
